import React, { useState } from 'react';
import { FormGroup, Label, Input, Button, Collapse } from 'reactstrap';
import i18n from '@/utils/i18n';
import {
  IdName,
  OrderFulfillmentFilter,
  OrderFulfillmentStatus,
  OrderFulfillmentType,
} from '../../../graphql/generatedTypes';
import { FaAngleDown, FaAngleRight } from 'react-icons/fa';
import ContainerTypeSelector from '../../../components/ContainerType/ContainerTypeSelector';
import { ContainerType } from '../OrderFulfillment/Components/OrderFulfillmentAddContainerTypes';
import { DatePickerInput, DatesProvider } from '@mantine/dates';

type OrderFulfillmentFilterFormProps = {
  filter: OrderFulfillmentFilter;
  handleFilterSubmit: (filter: OrderFulfillmentFilter) => void;
  authorizedSigners: IdName[];
  events: IdName[];
  itemLocations: IdName[];
  eventAccounts: IdName[];
  containerTypes?: ContainerType[];
  containerTypesUsedInEvent?: number[];
};

const OrderFulfillmentFilterForm = ({
  filter: initialFilter,
  handleFilterSubmit,
  authorizedSigners,
  events,
  itemLocations,
  eventAccounts,
  containerTypes,
  containerTypesUsedInEvent,
}: OrderFulfillmentFilterFormProps) => {
  const currentDate = new Date();

  const [filter, setFilter] = useState<OrderFulfillmentFilter>({
    ...initialFilter,
  });

  const containerTypeIds =
    filter.container_type_ids?.map((id) => {
      return id.toString();
    }) || ([] as string[]);

  const [isOpen, setIsOpen] = useState(false);
  const [containerTypeSelected, setContainerTypeSelected] =
    useState<string[]>(containerTypeIds);
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>();

  const toggle = () => setIsOpen(!isOpen);

  const handleFilterChange = (e: any) => {
    const { name, value } = e.target;
    console.log({ e, name, value });
    setFilter((prevState) => ({
      ...prevState,
      [name]: value === 'null' ? null : value,
    }));
  };

  function onSubmit(e: any) {
    handleFilterSubmit({
      ...filter,
      start_date:
        filter.start_date != null && filter.start_date.length > 0
          ? new Date(filter.start_date).toISOString()
          : null,
      end_date:
        filter.end_date != null && filter.end_date.length > 0
          ? new Date(filter.end_date).toISOString()
          : null,
    });
  }

  const containerTypesFiltered = containerTypes?.filter((ct) => {
    if (
      containerTypesUsedInEvent?.length &&
      containerTypesUsedInEvent?.length > 0
    ) {
      return containerTypesUsedInEvent?.includes(ct.id);
    } else {
      return true;
    }
  });

  return (
    <div className="mt-2">
      <h5>{i18n.t('OrderFulfillment:filter')}</h5>
      <div>
        <Button onClick={toggle} className="float-end ms-2 mt-2 mb-2">
          {isOpen ? <FaAngleDown /> : <FaAngleRight />}
        </Button>
      </div>

      <Collapse style={{ clear: 'both' }} isOpen={isOpen}>
        <form>
          <FormGroup>
            <Label for="type">{i18n.t('OrderFulfillment:type')}</Label>
            <Input
              type="select"
              name="type"
              id="type"
              value={filter.type || 'null'}
              onChange={handleFilterChange}
            >
              <option value="null">{i18n.t('Common:all')}</option>
              {Object.values(OrderFulfillmentType).map((type) => (
                <option key={type} value={type}>
                  {i18n.t(`OrderFulfillmentType:${type?.toUpperCase()}`)}
                </option>
              ))}
            </Input>
          </FormGroup>

          <FormGroup>
            <Label for="status">{i18n.t('OrderFulfillment:status')}</Label>

            <Input
              type="select"
              name="status"
              id="status"
              value={filter.status || 'all'}
              onChange={handleFilterChange}
            >
              <option value="null">{i18n.t('Common:all')}</option>

              {Object.values(OrderFulfillmentStatus).map((status) => (
                <option key={status} value={status}>
                  {i18n.t(`OrderFulfillment:status_${status}`)}
                </option>
              ))}
            </Input>
          </FormGroup>

          {containerTypesFiltered && (
            <FormGroup>
              <Label for="type">
                {i18n.t('OrderFulfillment:containerType')}
              </Label>

              <ContainerTypeSelector
                data={containerTypesFiltered}
                value={containerTypeSelected}
                onChange={(value) => {
                  if (Array.isArray(value)) {
                    let parsedValue = value;

                    if (parsedValue === null) {
                      parsedValue = [];
                    }

                    const parsedValueNumbers = parsedValue.map((val) => {
                      return Number(val);
                    });

                    setContainerTypeSelected(parsedValue);
                    handleFilterChange({
                      target: {
                        name: 'container_type_ids',
                        value: parsedValueNumbers,
                      },
                    });
                  }
                }}
                searchable
                clearable
              />
            </FormGroup>
          )}

          <FormGroup>
            <Label for="date_range">
              {i18n.t('OrderFulfillment:bookingPeriod')}
            </Label>

            <DatesProvider settings={{ locale: i18n.language }}>
              <DatePickerInput
                type="range"
                value={dateRange}
                maxDate={currentDate}
                onChange={(dates) => {
                  setDateRange(dates);

                  const [startDate, endDate] = dates;

                  setFilter((prevState) => ({
                    ...prevState,
                    start_date: startDate?.toISOString(),
                    end_date: endDate?.toISOString(),
                  }));
                }}
                numberOfColumns={2}
                hideOutsideDates={false}
                clearable
              />
            </DatesProvider>
          </FormGroup>

          <FormGroup>
            <Label for="event_id">{i18n.t('OrderFulfillment:event')}</Label>
            <Input
              type="select"
              name="event_id"
              id="event_id"
              value={filter.event_id || 'null'}
              onChange={handleFilterChange}
              disabled={events.length === 1}
            >
              <option value="null">{i18n.t('Common:all')}</option>
              {events.map((event) => (
                <option key={event.id} value={event.id}>
                  {event.name}
                </option>
              ))}
            </Input>
          </FormGroup>

          <FormGroup>
            <Label for="event_account_id">
              {i18n.t('OrderFulfillment:eventAccount')}
            </Label>
            <Input
              type="select"
              name="event_account_id"
              id="event_account_id"
              value={filter.event_account_id || 'null'}
              onChange={handleFilterChange}
              disabled={eventAccounts.length === 1}
            >
              <option value="null">{i18n.t('Common:all')}</option>
              {eventAccounts.map((account) => (
                <option key={account.id} value={account.id}>
                  {account.name}
                </option>
              ))}
            </Input>
          </FormGroup>

          <FormGroup>
            <Label for="item_location_id">
              {i18n.t('OrderFulfillment:itemLocation')}
            </Label>
            <Input
              type="select"
              name="item_location_id"
              id="item_location_id"
              value={filter.item_location_id || 'null'}
              onChange={handleFilterChange}
              disabled={itemLocations.length === 1}
            >
              <option value="null">{i18n.t('Common:all')}</option>
              {itemLocations.map((location) => (
                <option key={location.id} value={location.id}>
                  {location.name}
                </option>
              ))}
            </Input>
          </FormGroup>

          <FormGroup>
            <Label for="authorized_signer_id">
              {i18n.t('OrderFulfillment:authorizedSigner')}
            </Label>
            <Input
              type="select"
              name="authorized_signer_id"
              id="authorized_signer_id"
              value={filter.authorized_signer_id || 'null'}
              onChange={handleFilterChange}
              disabled={authorizedSigners.length === 1}
            >
              <option value="null">{i18n.t('Common:all')}</option>
              {authorizedSigners.map((signer) => (
                <option key={signer.id} value={signer.id}>
                  {signer.name}
                </option>
              ))}
            </Input>
          </FormGroup>

          {/* Add similar form groups for other filter fields */}
          <Button onClick={onSubmit}>
            {i18n.t('OrderFulfillment:applyFilters')}
          </Button>
        </form>
      </Collapse>
    </div>
  );
};

export default OrderFulfillmentFilterForm;
